import type { Route } from 'next';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
	createContext,
	PropsWithChildren,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Temporal } from 'temporal-polyfill';

import { CloseButton } from '@calm-web/design-system';

import { useAnalytics } from '@/hooks/analytics/useAnalytics';
import { usePromoOfferEnabledState, usePromoOfferState, useUserState } from '@/hooks/store';
import { PromoOffer } from '@/server/initState/getPromoOffer';
import { setPromoOffer } from '@/store/promoOffer/actions';
import apiRequest from '@/utils/apiRequest';
import { getUserData } from '@/utils/apiRequest/getUserData';
import { allowsEvaluationDateOverride } from '@/utils/promoOffer/allowsEvaluationDateOverride';
import { buildSurface } from '@/utils/promoOffer/buildSurface';
import { isPromoOffer } from '@/utils/promoOffer/isPromoOffer';
import ArrowRight from 'icons/right-arrow.svg';

import { messages } from './messages';
import {
	BannerArrow,
	BannerMessaging,
	BannerSubtitle,
	BannerTitleGroup,
	CloseWrapper,
	CTAText,
	StyledBanner,
} from './styles';
import { OfferBannerContextProps } from './types';

const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

export const OfferBannerContext = createContext<OfferBannerContextProps>({
	isBannerVisible: false,
});

// Legacy key from before dismissals moved to one key per offer — still read (never written)
// so a dismissal recorded by an older build stays honored.
const LEGACY_DISMISSED_OFFER_KEY = 'dismissedPromoOfferId';
// One key per dismissed offer, rather than one JSON array under a shared key, so a
// read-modify-write race between two tabs dismissing different offers at once can't drop
// either dismissal — each tab only ever touches its own offer's key.
const DISMISSED_OFFER_KEY_PREFIX = 'dismissedPromoOffer:';
// Bounds the client refetch end to end.
const REFETCH_TIMEOUT_MS = 1500;
const EMPTY_DISMISSED_OFFER_IDS: string[] = [];

type StoredDismissal = { id: string; ends_at: string | null };

function parseLegacyDismissals(raw: string | null): StoredDismissal[] {
	if (!raw) return [];
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed.flatMap((entry: unknown): StoredDismissal[] => {
			if (typeof entry === 'string') return [{ id: entry, ends_at: null }];
			if (typeof entry === 'object' && entry !== null && typeof (entry as { id?: unknown }).id === 'string') {
				const { id, ends_at } = entry as { id: string; ends_at?: unknown };
				return [{ id, ends_at: typeof ends_at === 'string' ? ends_at : null }];
			}
			return [];
		});
	} catch {
		// Legacy value: a bare offer id string written before this key held a JSON array —
		// treat it as a one-element array rather than dropping the dismissal.
		return [{ id: raw, ends_at: null }];
	}
}

function isDismissalStillRelevant(endsAt: string | null, nowMs: number): boolean {
	if (endsAt === null) return true;
	try {
		return Temporal.Instant.from(endsAt).epochMilliseconds > nowMs;
	} catch {
		return true;
	}
}

// Caches the parsed array by a key built from the FILTERED result (the still-relevant ids),
// not the raw storage contents, so repeated calls with unchanged storage return the same
// array reference — useSyncExternalStore compares getSnapshot's result with Object.is, and a
// fresh array on every call (even with identical contents) would never compare equal and
// would re-render in an infinite loop. Keying off the filtered result (rather than the raw
// strings) also means a stored dismissal whose ends_at passes between calls, with no storage
// write at all, changes the key and produces a new array instead of caching a stale one.
let lastCacheKey: string | undefined;
let lastParsedIds: string[] = EMPTY_DISMISSED_OFFER_IDS;

// SSR-safe: localStorage throws in some browser privacy modes, and doesn't exist at all
// during server rendering.
function getDismissedOfferIds(): string[] {
	let legacyRaw: string | null;
	const perOfferEntries: [string, string | null][] = [];
	try {
		legacyRaw = localStorage.getItem(LEGACY_DISMISSED_OFFER_KEY);
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key?.startsWith(DISMISSED_OFFER_KEY_PREFIX)) {
				perOfferEntries.push([key, localStorage.getItem(key)]);
			}
		}
	} catch {
		return EMPTY_DISMISSED_OFFER_IDS;
	}

	const nowMs = Temporal.Now.instant().epochMilliseconds;
	const legacyIds = parseLegacyDismissals(legacyRaw)
		.filter(entry => isDismissalStillRelevant(entry.ends_at, nowMs))
		.map(entry => entry.id);
	const perOfferIds = perOfferEntries
		.filter(([, value]) => isDismissalStillRelevant(value === 'null' || !value ? null : value, nowMs))
		.map(([key]) => key.slice(DISMISSED_OFFER_KEY_PREFIX.length));

	const ids = [...legacyIds, ...perOfferIds];
	// Structural encoding: a joined string would let ids containing the delimiter collide.
	const cacheKey = JSON.stringify(ids);
	if (cacheKey === lastCacheKey) return lastParsedIds;
	lastCacheKey = cacheKey;
	lastParsedIds = ids;
	return lastParsedIds;
}

// Scans every stored dismissal (legacy and per-offer) for the soonest ends_at that hasn't
// passed yet, so the subscribe function can wake this tab exactly when a dismissal's
// relevance would otherwise change with no storage write to trigger the `storage` event.
function getEarliestFutureEndsAtMs(nowMs: number): number | null {
	let legacyRaw: string | null;
	const perOfferValues: (string | null)[] = [];
	try {
		legacyRaw = localStorage.getItem(LEGACY_DISMISSED_OFFER_KEY);
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key?.startsWith(DISMISSED_OFFER_KEY_PREFIX)) {
				perOfferValues.push(localStorage.getItem(key));
			}
		}
	} catch {
		return null;
	}

	const endsAtValues = [
		...parseLegacyDismissals(legacyRaw).map(entry => entry.ends_at),
		...perOfferValues.map(value => (value === 'null' || !value ? null : value)),
	];

	let earliest: number | null = null;
	for (const endsAt of endsAtValues) {
		if (endsAt === null) continue;
		let epochMs: number;
		try {
			epochMs = Temporal.Instant.from(endsAt).epochMilliseconds;
		} catch {
			continue;
		}
		if (epochMs <= nowMs) continue;
		if (earliest === null || epochMs < earliest) earliest = epochMs;
	}
	return earliest;
}

function addDismissedOffer(offer: PromoOffer): void {
	try {
		const nowMs = Temporal.Now.instant().epochMilliseconds;
		// Prune expired per-offer keys on write rather than on read, so storage doesn't grow
		// unbounded with offers nobody will ever see again.
		for (let i = localStorage.length - 1; i >= 0; i--) {
			const key = localStorage.key(i);
			if (!key?.startsWith(DISMISSED_OFFER_KEY_PREFIX)) continue;
			const value = localStorage.getItem(key);
			if (!isDismissalStillRelevant(value === 'null' || !value ? null : value, nowMs)) {
				localStorage.removeItem(key);
			}
		}
		localStorage.setItem(`${DISMISSED_OFFER_KEY_PREFIX}${offer.id}`, offer.ends_at || 'null');
	} catch {
		// Ignore — worst case the banner reappears after a dismiss in this session.
	}
}

// A dismissal written by another tab never fires a local setState, so this tab only learns
// about it via the browser's `storage` event (which — unlike localStorage writes — does
// fire across tabs on the same origin). `key === null` signals a full clear (e.g.
// localStorage.clear()), which also needs to resync this tab's view.
function subscribeToStorage(callback: () => void): () => void {
	// A stored dismissal's relevance can change purely from time passing, with no storage
	// write to fire a `storage` event. useSyncExternalStore calls this subscribe function
	// once per mount, so a single one-shot timer would only ever wake for the dismissal
	// that expires soonest; each firing reschedules for whatever now-soonest expiry
	// remains, so later dismissals still get their own wake-up. A storage write — including
	// one from another tab — can also change which expiry is soonest, so it reschedules
	// too rather than only notifying. Capped like the offer expiry timer to avoid the
	// 32-bit setTimeout overflow.
	let expiryTimer: ReturnType<typeof setTimeout> | undefined;
	const scheduleNextExpiryWakeup = () => {
		clearTimeout(expiryTimer);
		try {
			const nowMs = Temporal.Now.instant().epochMilliseconds;
			const earliestFutureEndsAtMs = getEarliestFutureEndsAtMs(nowMs);
			if (earliestFutureEndsAtMs !== null) {
				expiryTimer = setTimeout(
					() => {
						callback();
						scheduleNextExpiryWakeup();
					},
					Math.min(earliestFutureEndsAtMs - nowMs, MAX_TIMER_DELAY_MS),
				);
			}
		} catch {
			// Ignore — worst case a stale dismissal stays honored until the next storage event.
		}
	};
	const onStorage = (event: StorageEvent) => {
		if (
			event.key === null ||
			event.key === LEGACY_DISMISSED_OFFER_KEY ||
			event.key?.startsWith(DISMISSED_OFFER_KEY_PREFIX)
		) {
			callback();
			scheduleNextExpiryWakeup();
		}
	};
	window.addEventListener('storage', onStorage);
	scheduleNextExpiryWakeup();

	return () => {
		window.removeEventListener('storage', onStorage);
		clearTimeout(expiryTimer);
	};
}

function useStoredDismissedOfferIds(): string[] {
	return useSyncExternalStore(subscribeToStorage, getDismissedOfferIds, () => EMPTY_DISMISSED_OFFER_IDS);
}

// Merges the current page's utm_* params into destinationUrl (only where destinationUrl
// doesn't already set that key) so a visitor who arrived via a campaign link carries that
// attribution through the banner's own navigation, matching what the old useRouterPush-based
// link did by forwarding the whole query string.
function withCurrentUtmParams(destinationUrl: string, currentSearch: string): string {
	const currentParams = new URLSearchParams(currentSearch);
	const utmEntries = [...currentParams.entries()].filter(([key]) => key.startsWith('utm_'));
	if (utmEntries.length === 0) return destinationUrl;

	// A fragment is split off first so it never ends up encoded into the query.
	const hashIndex = destinationUrl.indexOf('#');
	const hash = hashIndex === -1 ? '' : destinationUrl.slice(hashIndex);
	const withoutHash = hashIndex === -1 ? destinationUrl : destinationUrl.slice(0, hashIndex);
	// Split at the first separator only; a query value may itself contain a question mark.
	const queryIndex = withoutHash.indexOf('?');
	const path = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);
	const query = queryIndex === -1 ? '' : withoutHash.slice(queryIndex + 1);
	const destinationParams = new URLSearchParams(query);
	for (const [key, value] of utmEntries) {
		if (!destinationParams.has(key)) destinationParams.set(key, value);
	}

	const mergedQuery = destinationParams.toString();
	return `${mergedQuery ? `${path}?${mergedQuery}` : path}${hash}`;
}

export const BannerComponent = ({ offer, onClose }: { offer: PromoOffer; onClose: () => void }) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const router = useRouter();
	const searchParams = useSearchParams();
	const currentSearch = searchParams?.toString() ?? '';

	const analyticsProps = {
		offer_id: offer.id,
		period: offer.period,
		// Drop the query: it can carry personal or campaign data the surface itself doesn't need.
		surface: offer.surface.split('?')[0],
		coupon: offer.coupon,
		pack_class: offer.analytics.pack_class,
		flavor: offer.analytics.flavor,
		variant: offer.variant ?? offer.analytics.flavor,
	};

	return (
		<div role="region" aria-labelledby="banner-copy">
			<StyledBanner
				href={offer.destination_url}
				onClick={e => {
					e.preventDefault();
					logEvent({ eventName: 'Web Banner : Clicked', eventProps: analyticsProps });
					// destination_url is api-supplied, not a statically known Route literal.
					router.push(withCurrentUtmParams(offer.destination_url, currentSearch) as Route);
				}}
			>
				<BannerMessaging>
					<BannerTitleGroup>
						<span id="banner-copy">{offer.title}</span>
						{offer.subtitle && <BannerSubtitle>{offer.subtitle}</BannerSubtitle>}
					</BannerTitleGroup>
					&nbsp;
					<CTAText>{offer.cta_label}</CTAText>
					<BannerArrow>
						<ArrowRight height="24px" width="24px" viewBox="0 0 24 18" />
					</BannerArrow>
				</BannerMessaging>
			</StyledBanner>
			{/* Rendered as a sibling of the link, not a nested button, so dismissing never
				bubbles into the anchor's own click handler (which would log a spurious
				Clicked event and navigate). */}
			<CloseWrapper>
				<CloseButton
					aria-label={offer.dismiss_label || formatMessage(messages.dismissAriaLabel)}
					color="white"
					onPress={onClose}
				/>
			</CloseWrapper>
		</div>
	);
};

const OfferBannerProvider = ({ children }: PropsWithChildren) => {
	const storedDismissedIds = useStoredDismissedOfferIds();
	// A dismissal in this session wins over whatever was already in storage at mount —
	// set directly in the click handler (not an effect), so no synchronous setState-in-effect.
	// Session dismissals carry the offer's end so they follow the same lifetime as the
	// persisted ones: a later response reusing the id with a new end is not suppressed.
	const [sessionDismissals, setSessionDismissals] = useState<StoredDismissal[]>([]);
	const dismissedIds = useMemo(() => {
		if (!sessionDismissals.length) return storedDismissedIds;
		const nowMs = Temporal.Now.instant().epochMilliseconds;
		const liveSessionIds = sessionDismissals
			.filter(entry => isDismissalStillRelevant(entry.ends_at, nowMs))
			.map(entry => entry.id);
		return [...storedDismissedIds, ...liveSessionIds];
	}, [storedDismissedIds, sessionDismissals]);
	// The `${id}|${ends_at}` of the offer whose expiry timer has fired. Keying by instant as
	// well as id means a later response reusing the same id with a new (later) ends_at is not
	// mistaken for the offer that already expired.
	const [expiredOfferKey, setExpiredOfferKey] = useState<string | null>(null);
	const offer = usePromoOfferState();
	const promoOfferEnabled = usePromoOfferEnabledState();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const searchParamsString = searchParams?.toString() ?? '';
	const dispatch = useDispatch();
	const { logEvent, isAnalyticsInitialized } = useAnalytics();
	// Guards the "viewed" event so it fires once per offer id per page load, not on
	// every re-render while the offer stays visible.
	const viewedOfferIds = useRef(new Set<string>());
	// The mount fetch and an auth-transition refetch can both be in flight at once; a
	// response only gets dispatched if it's still the most recent request issued, so a
	// stale response arriving after a newer one can't overwrite it.
	const latestRequestId = useRef(0);
	// The backend decides the audience from the subscription, so the offer is re-asked
	// whenever the identity or its subscription standing changes, not only on login/logout.
	// A purchase in this session dispatches a user with a valid subscription; without this
	// the pre-purchase offer would keep rendering until a reload.
	const user = useUserState();
	const userId = user?.id;
	const isFreeLoggedInUser = Boolean(userId) && !user?.subscription?.valid;
	const audienceKey = [
		user?.id ?? '',
		user?.subscription?.valid ?? '',
		user?.subscription?.expires ?? '',
	].join('|');
	// Scopes the offer to both audience and surface, so a soft navigation to another page
	// hides the previous page's offer immediately instead of leaving it visible until the
	// new surface's refetch settles. If pathname hasn't resolved yet (Pages Router before
	// hydration), the surface segment is empty; once pathname resolves the key changes and
	// the offer hides until the mount refetch settles, which is expected.
	const currentScopeKey = useMemo(
		() =>
			`${audienceKey}|${pathname === null ? '' : buildSurface(pathname, new URLSearchParams(searchParamsString))}`,
		[audienceKey, pathname, searchParamsString],
	);
	// The scope a currently-stored offer was fetched for. Comparing this to the live
	// currentScopeKey (rather than an in-flight boolean) hides a stale offer the instant the
	// audience or surface changes, in the same render, with no effect-lag window where a
	// stale offer stays clickable. It starts equal to currentScopeKey so the SSR offer stays
	// visible on mount — except on the Pages Router's first client render, where pathname is
	// still null and currentScopeKey is scoped to an empty surface that doesn't match
	// anything real; `null` there means "server-provided, not yet scoped" (treated as
	// visible below) so the SSR offer doesn't flicker out before the router resolves.
	const [offerScopeKey, setOfferScopeKey] = useState<string | null>(() =>
		pathname === null ? null : currentScopeKey,
	);
	const currentScopeKeyRef = useRef(currentScopeKey);
	currentScopeKeyRef.current = currentScopeKey;

	useEffect(() => {
		if (!offer) return;
		let endsAt: number;
		try {
			endsAt = Temporal.Instant.from(offer.ends_at).epochMilliseconds;
		} catch {
			return;
		}

		let timer: ReturnType<typeof setTimeout>;
		// A delay over 2^31-1 ms (about 24.8 days) overflows the timer and fires at once,
		// which would hide a month-long offer immediately — so a remaining wait longer than
		// that is chunked into successive re-checks, each capped at the same limit, instead
		// of one setTimeout that overflows.
		const checkExpiry = () => {
			const msRemaining = endsAt - Temporal.Now.instant().epochMilliseconds;
			if (msRemaining <= 0) {
				setExpiredOfferKey(`${offer.id}|${offer.ends_at}`);
				return;
			}
			timer = setTimeout(checkExpiry, Math.min(msRemaining, MAX_TIMER_DELAY_MS));
		};
		// Always async (even a 0ms delay) so an already-past ends_at still resolves via the
		// timer callback rather than a synchronous setState call in the effect body.
		timer = setTimeout(checkExpiry, 0);
		return () => clearTimeout(timer);
	}, [offer]);

	const refetchOffer = useCallback(async () => {
		if (typeof window === 'undefined' || !promoOfferEnabled || !userId) return;
		// A null pathname means the router hasn't resolved one yet — there's no surface to
		// request against.
		if (pathname === null) return;
		const requestScopeKey = currentScopeKeyRef.current;
		const requestId = ++latestRequestId.current;
		try {
			// buildSurface copies whatever URLSearchParams it's given, so this copy only
			// needs to isolate the evaluation_date read below from that internal copy.
			const surfaceSearchParams = new URLSearchParams(searchParams?.toString() ?? '');
			// Read separately: evaluation_date is sent as its own QA override param while
			// buildSurface strips it from the surface the api matches on.
			const evaluationDate = surfaceSearchParams.get('evaluation_date');
			const surface = buildSurface(pathname, surfaceSearchParams);
			let endpoint = `promo-offers?surface=${encodeURIComponent(surface)}`;
			// QA-only override for SSR's "now" (see buildSurface) — non-production only,
			// and kept out of `surface` itself so it never affects offer matching.
			if (allowsEvaluationDateOverride() && evaluationDate) {
				endpoint += `&evaluation_date=${encodeURIComponent(evaluationDate)}`;
			}
			// The axios timeout does not bound useApi's own error path, which can await a
			// visitor-driven reCAPTCHA and retry. Racing the whole call keeps the 1.5s contract
			// so a blocked request clears the stale offer instead of leaving it visible.
			let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
			const deadline = new Promise<never>((_resolve, reject) => {
				deadlineTimer = setTimeout(
					() => reject(new Error('Timed out waiting for promo offer')),
					REFETCH_TIMEOUT_MS,
				);
			});
			let result: { data?: { offer?: unknown } } | undefined;
			try {
				// Calls apiRequest directly rather than useApi: useApi would log this
				// endpoint (which carries the surface) in a PX analytics event, await a
				// visitor-driven reCAPTCHA and retry on a PX block, and redirect the whole
				// page to /server-maintenance on a 542 — all wrong for a best-effort banner.
				result = await Promise.race([
					apiRequest<{ offer: unknown }>({
						endpoint,
						method: 'GET',
						userData: getUserData(),
						timeout: REFETCH_TIMEOUT_MS,
						// Same endpoint for every audience — without this, a cached anonymous
						// response can be replayed after the visitor's auth state changes.
						noCache: true,
					}),
					deadline,
				]);
			} finally {
				clearTimeout(deadlineTimer);
			}
			if (latestRequestId.current === requestId) {
				const candidate = result?.data?.offer;
				dispatch(setPromoOffer(isPromoOffer(candidate) ? candidate : null));
				setOfferScopeKey(requestScopeKey);
			}
		} catch {
			// A stale SSR-anonymous offer must not outlive a refetch that failed after
			// the user's auth state changed, so clear it rather than leaving it visible.
			if (latestRequestId.current === requestId) {
				dispatch(setPromoOffer(null));
				setOfferScopeKey(requestScopeKey);
			}
		}
	}, [dispatch, pathname, promoOfferEnabled, searchParams, userId]);

	useEffect(() => {
		// refetchOffer already no-ops when promoOfferEnabled is false or pathname is null,
		// so this fires on every audience change, every surface navigation, and every
		// opt-in transition without needing separate guards here.
		refetchOffer().catch(() => undefined);
	}, [audienceKey, promoOfferEnabled, pathname, searchParamsString, refetchOffer]);

	const isExpired = Boolean(offer && `${offer.id}|${offer.ends_at}` === expiredOfferKey);
	const isBannerVisible =
		isFreeLoggedInUser &&
		Boolean(offer) &&
		!dismissedIds.includes(offer?.id ?? '') &&
		!isExpired &&
		(offerScopeKey === null || offerScopeKey === currentScopeKey);

	useEffect(() => {
		// Keyed by scope + offer id, not offer id alone, so the same offer id shown again on
		// a different marketing surface after a soft navigation logs its own Viewed event
		// instead of being silently deduped against the first surface's impression. Withheld
		// entirely while pathname is still null (Pages Router pre-hydration): currentScopeKey's
		// placeholder empty surface there would otherwise log one Viewed event for it and a
		// second, spurious one once the real surface resolves — double-counting one impression.
		const viewedKey = offer && pathname !== null ? `${currentScopeKey}|${offer.id}` : null;
		// Before consent the analytics client drops events, so recording the id then would
		// silence the impression for the rest of the page once consent arrives.
		if (
			isAnalyticsInitialized &&
			isBannerVisible &&
			offer &&
			viewedKey &&
			!viewedOfferIds.current.has(viewedKey)
		) {
			viewedOfferIds.current.add(viewedKey);
			logEvent({
				eventName: 'Web Banner : Viewed',
				eventProps: {
					offer_id: offer.id,
					period: offer.period,
					// Drop the query: it can carry personal or campaign data the surface itself doesn't need.
					surface: offer.surface.split('?')[0],
					coupon: offer.coupon,
					pack_class: offer.analytics.pack_class,
					flavor: offer.analytics.flavor,
					variant: offer.variant ?? offer.analytics.flavor,
				},
			});
			// The api only fires the assignment exposure that tags the user with the arm. This event is
			// the custom exposure the experiment analysis counts, with keys mirroring mobile.
			if (offer.variant) {
				logEvent({
					eventName: 'Upsell : Product : Seen',
					eventProps: {
						screen: 'Web Banner',
						source: 'web_banner',
						data_source: 'server',
						upsell_location: offer.surface.split('?')[0],
						pack_class: offer.analytics.pack_class,
						flavor: offer.analytics.flavor,
						variant: offer.variant,
						coupon: offer.coupon,
					},
				});
			}
		}
	}, [isAnalyticsInitialized, isBannerVisible, offer, logEvent, currentScopeKey, pathname]);

	const onClose = useCallback(() => {
		if (!offer) return;
		const offerId = offer.id;
		// Replace (not skip) an existing entry for this id: a same-id offer renewed with a
		// later ends_at must be dismissed under its new expiry, not the stale one already stored.
		setSessionDismissals(prev => [
			...prev.filter(entry => entry.id !== offerId),
			{ id: offerId, ends_at: offer.ends_at },
		]);
		addDismissedOffer(offer);
		logEvent({
			eventName: 'Web Banner : Dismissed',
			eventProps: {
				offer_id: offer.id,
				period: offer.period,
				// Drop the query: it can carry personal or campaign data the surface itself doesn't need.
				surface: offer.surface.split('?')[0],
				coupon: offer.coupon,
				pack_class: offer.analytics.pack_class,
				flavor: offer.analytics.flavor,
				variant: offer.variant ?? offer.analytics.flavor,
			},
		});
	}, [offer, logEvent]);

	const value = useMemo(
		() => ({
			isBannerVisible,
		}),
		[isBannerVisible],
	);

	return (
		<OfferBannerContext.Provider value={value}>
			{children}
			{isBannerVisible && offer && <BannerComponent offer={offer} onClose={onClose} />}
		</OfferBannerContext.Provider>
	);
};

export default OfferBannerProvider;
